import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withRouter } from 'react-router-dom';

// MUI
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';

// CUSTOM
import QButton from 'components/QButton';
import QCheck from 'components/QCheck';
import FlowDialog from 'components/FlowDialog';
import RecurringPage from 'components/GettingStartedAcme/Recurring/RecurringPage';
import AccountPage from 'components/GettingStartedAcme/Accounts/AccountPage';

// DATA
import { getInstitutionsById } from 'data/institutions/selectors';
import { getIdsRefreshing } from 'data/institutionLogins/selectors';
import { getAccountsById } from 'data/accounts/selectors';
import { getSpendingWatchLists } from 'data/spendingWatchList/selectors';
import { getScheduledTransactions as getScheduledTransactionsAction } from 'data/scheduledTransactions/scheduledTransactionsActions';
import { getBillsAndIncome } from 'data/scheduledTransactions/scheduledTransactionsSelectors';
import { getProfile } from 'data/profile/selectors';
import { updateProfile } from 'data/profile/actions';
import { getDatasetId } from 'utils/auth/selectors';
import { getDatasetsById } from 'data/datasets/selectors';
import { updateDataset } from 'data/datasets/actions';
import { setPreference } from 'data/preferences/actions';
import { getSharedPreferencesByPath } from 'data/preferences/selectors';
import * as spendingWatchListActions from 'data/spendingWatchList/actions';
import * as categoriesselectors from 'data/categories/selectors';
import { mkFilter } from 'data/filters/types';

import { getLogger } from 'utils/logger';
import tracker from 'utils/tracker';

import { Map } from 'immutable';
import { v4 as uuidv4 } from 'uuid';
import styles from './naturalStyles';

const logger = getLogger('setupPageAcme/naturalFlow.js');


class NaturalFlow extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      firstName: '',
      lastName: '',
      zipcode: '',
      ...this.updatePrefilledUserInfo(this.props),
      comingFrom: 0, // last state value user is navigating from
    };
  }

  // I know these are supposed to be phased out - but it works really well for my specific use-case of needing
  // to populate state when the redux values come in.
  // getDerivedStateFromProps and didUpdate doesn't work as it would reset these state values on every stateful change if
  // I didn't memoize the values - but doing that would be overkill for the situation here.
  UNSAFE_componentWillReceiveProps(nextProps) { // eslint-disable-line camelcase
    if (nextProps.profile && nextProps.profile !== this.props.profile) {
      this.setState(this.updatePrefilledUserInfo(nextProps));
    }
  }

  componentDidUpdate(prevProps) {
    const { aggLoading, setupStateIndex } = this.props;
    if (setupStateIndex === 3 && !aggLoading) {
      if (prevProps.aggLoading) {
        setTimeout(this.evalAutoProceed, 1200);
      } else if (this.state.comingFrom <= 3) {
        setTimeout(this.evalAutoProceed, 180);
      } else {
        setTimeout(this.evalAutoRetreat, 180);
      }
    }
  }

  evalAutoProceed = () => {
    const { aggLoading, setupStateIndex } = this.props;
    if (setupStateIndex === 3 && !aggLoading) {
      this.proceed();
    }
  };
  evalAutoRetreat = () => {
    const { aggLoading, setupStateIndex } = this.props;
    if (setupStateIndex === 3 && !aggLoading) {
      this.goBack();
    }
  };

  updatePrefilledUserInfo = (props) => {
    let stateToMerge = {};
    const { profile } = props;
    if (profile) {
      const { primaryAddress } = profile;
      if (primaryAddress) {
        if (primaryAddress.zipCode) {
          stateToMerge = { ...stateToMerge, zipcode: profile.primaryAddress.zipCode };
        }
        if (primaryAddress.fullName) {
          const names = primaryAddress.fullName.split(' ');
          stateToMerge = { ...stateToMerge, firstName: names[0], lastName: names[names.length-1]};
        }
      }
      if (profile.firstName) {
        stateToMerge = { ...stateToMerge, firstName: profile.firstName};
      }
      if (profile.lastName) {
        stateToMerge = { ...stateToMerge, lastName: profile.lastName};
      }
    }
    return stateToMerge;
  };


  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                                  Functions                                      //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //

  proceed = () => {
    const { setupStateIndex } = this.props;
    switch (setupStateIndex) {
      case 1: { // personal info screen
        const { profile, dispatchUpdateProfile, dataset, dispatchUpdateDataset } = this.props;
        if (profile) {
          tracker.track(tracker.events.firstName);
          tracker.track(tracker.events.lastName);
          tracker.track(tracker.events.zipCode);

          const newProf = profile.toJS() || {};
          let dirty = false;
          if (!newProf.primaryAddress) {
            dirty = true;
            newProf.primaryAddress = { zipCode: this.state.zipcode };
          } else {
            dirty = newProf.primaryAddress.zipCode !== this.state.zipcode;
            newProf.primaryAddress.zipCode = this.state.zipcode;
          }
          if (newProf.firstName !== this.state.firstName) {
            dirty = true;
            newProf.firstName = this.state.firstName;
          }
          if (newProf.lastName !== this.state.lastName) {
            dirty = true;
            newProf.lastName = this.state.lastName;
          }
          if (dirty) {
            dispatchUpdateProfile(newProf);
            dataset && dispatchUpdateDataset(dataset.set('zipcode', this.state.zipcode));
          }
        } else {
          logger.error(" NO PROFILE - Won't fire update \n(This really shouldn't happen, you know!)\n - Tell Chris Plz -");
        }
        this.advance();
        break;
      }
      case 3: { // Bill Scout waiter
        this.props.dispatchGetScheduledTransactionsAction();
        this.advance();
        break;
      }
      case 6: { // final screen (go to dashboard)
        this.finish();
        break;
      }
      default: {
        this.advance();
        break;
      }
    }
  };

  goBack = () => {
    const { setupStateIndex } = this.props;
    if (setupStateIndex !== 0) {
      this.setState({ comingFrom: setupStateIndex });
      this.props.dispatchSetDatasetPreference({ setupStateIndex: setupStateIndex - 1 });
    }
  };

  advance = () => {
    const { setupStateIndex } = this.props;
    this.setState({ comingFrom: setupStateIndex });
    this.props.dispatchSetDatasetPreference({ setupStateIndex: setupStateIndex + 1 });
  };

  finish = () => {
    // default watchlist creation
    const { watchlists } = this.props;
    if (watchlists.size === 0 && this.props.defaultWatchlistCategory) {
      const newId = uuidv4().toUpperCase();
      const selected = this.props.defaultWatchlistCategory.toArray();
      const filter = [{
        type: 'CHARTOFACCOUNT',
        chartOfAccounts: selected.map((select) => ({ id: select[0], type: 'CATEGORY' })),
      }];
      const defaultWatchlist = Map({
        title: "Dining & Drinks",
        targetAmount: null,
        filterItems: mkFilter({ id: null, clientId: newId, filterItems: filter }),
        emoji: 'U+1f60b',
      });
      this.props.createWatchList(defaultWatchlist);
    }

    // GSF wrap-up and mixpanel tracking
    const { numInstitutions } = this.props;
    this.props.dispatchSetDatasetPreference({ setupStateIndex: null });
    this.props.history.push('/');
    tracker.track(tracker.events.completeGettingStarted);
    tracker.track(tracker.events.fisAdded, { number: numInstitutions });
  };

  setFirstName = (e) => {
    this.setState({ firstName: e.currentTarget.value });
  };
  setLastName = (e) => {
    this.setState({ lastName: e.currentTarget.value });
  };
  setZip = (e) => {
    this.setState({ zipcode: e.currentTarget.value });
  };

  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                                  Renderers                                      //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //

  renderGreeting = () => {
    const { classes } = this.props;
    return (
      <div className={classes.centerWrapper}>
        <Typography variant="h4" style={{ marginBottom: 4}}>
          {"Hey there!"}
        </Typography>
        <Typography variant="h4">
          {"Welcome to Simplifi."}
        </Typography>
        <Typography style={{ margin: '40px 0'}}>
          {"You're on your way to taking control of your finances."}
        </Typography>
        <QButton
          id="getStarted"
          variant="contained"
          onClick={this.proceed}
          className={classes.okButton}
        >
          {`Get Started`}
        </QButton>
      </div>
    );
  };

  renderNameZip = () => {
    const { classes, profile } = this.props;
    const haveName = Boolean(this.state.firstName) && Boolean(this.state.lastName);
    return (
      <React.Fragment key={profile}>
        <Typography variant="h6">
          {"Let's get to know you a little"}
        </Typography>
        <div className={classes.nameRow}>
          <TextField
            id="first_name_field"
            label="First Name"
            className={classes.nameField}
            margin="normal"
            variant="outlined"
            value={this.state.firstName}
            onChange={this.setFirstName}
            inputProps={{
              autofill: "off",
              autoComplete: "new-password",
            }}
          />
          <TextField
            id="last_name_field"
            label="Last Name"
            className={classes.nameField}
            margin="normal"
            variant="outlined"
            value={this.state.lastName}
            onChange={this.setLastName}
            inputProps={{
              autofill: "off",
              autoComplete: "new-password",
            }}
          />
        </div>
        <Typography
          variant="subtitle1"
          className={classes.grey}
          style={{ margin: '16px 0 24px 0', opacity: haveName ? 1 : 0, transition: 'opacity 0.3s ease 0.1s' }}
        >
          {"Great, now that we're on a first name basis..."}
        </Typography>

        <div style={{ opacity: haveName ? 1 : 0, transition: haveName ? 'opacity 0.4s ease 1.2s' : 'opacity 0.3s ease 0.1s' }}>
          <Typography variant="h6">
            {"What's your Zip Code?"}
          </Typography>
          <TextField
            id="zipcode_field"
            label="Zip Code"
            className={classes.zipField}
            margin="normal"
            variant="outlined"
            value={this.state.zipcode}
            onChange={this.setZip}
            disabled={!haveName}
          />
        </div>
      </React.Fragment>
    );
  };

  renderFILauncher = () =>
    <AccountPage
      accounts={this.props.accounts}
    />;

  renderBillScoutWaiter = () => {
    const { classes, aggLoading } = this.props;
    return (
      <div className={classes.centerWrapper} id="waiterSize">
        <Typography variant="h6">
          {aggLoading ? 'Hang Tight!' : 'All Right!'}
        </Typography>
        <Typography variant="body1" className={classes.grey}>
          {aggLoading ? 'We are finding your recurring bills and income.' : `We're done searching for your bills and income` }
        </Typography>
        <div className={classes.waitCenter}>
          {aggLoading ?
            <CircularProgress size={80} style={{ margin: 20 }}/>
            :
            <QCheck style={{ margin: 38}} size={44}/>
          }
          <Typography variant="body1" className={classes.grey}>
            {aggLoading ? 'This should only take a few moments.' : `Let's continue`}
          </Typography>
        </div>
      </div>
    );
  };


  renderIncome = () =>
    <RecurringPage
      scheduledTxns={this.props.income}
    />;

  renderBills = () =>
    <RecurringPage
      scheduledTxns={this.props.bills}
      isBill
    />;

  renderFinalRedirect = () => {
    const { classes } = this.props;

    return (
      <div className={classes.centerWrapper}>
        <Typography variant="h6">
          {"You're all set!"}
        </Typography>
        <div className={classes.waitCenter}>
          <QCheck style={{ margin: 16}} size={44}/>
        </div>
        <QButton
          id="lets-go-button"
          variant="contained"
          onClick={this.finish}
          className={classes.okButton}
        >
          {`Let's Go!`}
        </QButton>
      </div>
    );
  };


  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //
  //                              Main Render Function                               //
  // # # - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - # # //

  render() {
    const { accounts, aggLoading, setupStateIndex } = this.props;
    const renderSequence = [this.renderGreeting, this.renderNameZip, this.renderFILauncher,
      this.renderBillScoutWaiter, this.renderIncome, this.renderBills, this.renderFinalRedirect];
    const haveUPS = Boolean(this.state.firstName) && Boolean(this.state.lastName) && Boolean(this.state.zipcode);

    return (
      <FlowDialog
        flowState={setupStateIndex}
        renderList={renderSequence}
        goBack={this.goBack}
        goForward={this.proceed}
        disableBack={setupStateIndex === 0}
        disableForward={(setupStateIndex === 2 && accounts.size === 0) || (setupStateIndex === 1 && !haveUPS)
         || (setupStateIndex === 3 && aggLoading)}
        dialogStyle={this.props.classes.dialogStyle}
      />
    );
  }
}

NaturalFlow.propTypes = {
  classes: PropTypes.object,
  accounts: PropTypes.object,
  numInstitutions: PropTypes.number,
  aggLoading: PropTypes.bool,
  bills: PropTypes.object,
  income: PropTypes.object,
  dispatchGetScheduledTransactionsAction: PropTypes.func,
  history: PropTypes.object,
  profile: PropTypes.object,
  dispatchUpdateProfile: PropTypes.func,
  dataset: PropTypes.object,
  dispatchUpdateDataset: PropTypes.func,
  setupStateIndex: PropTypes.number,
  dispatchSetDatasetPreference: PropTypes.func,
  createWatchList: PropTypes.func,
  defaultWatchlistCategory: PropTypes.object,
  watchlists: PropTypes.object,
};

function mapStateToProps(state) {
  const billsIncomeObject = getBillsAndIncome(state);
  return {
    aggLoading: !getIdsRefreshing(state).isEmpty(),
    numInstitutions: getInstitutionsById(state).size,
    accounts: getAccountsById(state),
    bills: billsIncomeObject.bills,
    income: billsIncomeObject.income,
    profile: getProfile(state),
    dataset: getDatasetsById(state).get(getDatasetId(state)),
    setupStateIndex: getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] }).setupStateIndex || 0,
    defaultWatchlistCategory: categoriesselectors.getDefaultWatchlistCategory(state),
    watchlists: getSpendingWatchLists(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchGetScheduledTransactionsAction: () => dispatch(getScheduledTransactionsAction()),
    dispatchUpdateProfile: (profile) => dispatch(updateProfile(profile)),
    dispatchUpdateDataset: (dataset) => dispatch(updateDataset(dataset)),
    dispatchSetDatasetPreference: (pref) =>
      dispatch(setPreference({ section: 'shared', group: 'dataset', preference: { webApp: pref } })),
    createWatchList: (data) => dispatch(spendingWatchListActions.createWatchList(data)),
  };
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(withRouter(NaturalFlow));
